import type { ReactNode } from "react";
import { CredentialType } from "@api/shared-runtime/enums";
import { LICENSE_TYPE_LABELS } from "@api/shared-runtime/credential-vocabularies";
import type { LicenseType } from "@api/shared-runtime/credential-vocabularies";
import type { Credential } from "../../../types";
import {
  AddressRow,
  ChipsRow,
  DateRow,
  Fields,
  RepeatingSection,
  Row,
  Section,
} from "./detail-primitives";
import { addressLine, asDetails, money, type Details } from "./detail-values";
import { formatDate } from "../../../lib/format-date";

/** A date as a row value; null stays null so Section can drop the row. */
const dateValue = (value: unknown) =>
  value === null || value === undefined || value === ""
    ? null
    : formatDate(String(value));

/** Join the parts of a composite value, or null when none are present. */
const joined = (parts: unknown[], separator = " ") => {
  const line = parts.filter(Boolean).map(String).join(separator);
  return line || null;
};

/**
 * How each credential type shows its full contents.
 *
 * One generic label/value dump cannot serve twenty-eight types: a malpractice
 * claims history is a list of incidents, personal info is half a dozen
 * sub-records, and a driver's licence is a number and a date. Each type gets a
 * renderer that lays out what that credential actually is.
 *
 * The registry is an exhaustive `Record<CredentialType, …>` on purpose — adding
 * a credential type will not compile until someone decides how it reads. A
 * silent fallback is how a new type ends up displaying nothing for a year.
 *
 * Types whose data genuinely is a short flat list share `identityAndDates` and
 * friends; that is reuse of a layout, not a generic bucket.
 */

type Renderer = (input: {
  version: NonNullable<Credential["version"]>;
  details: Details;
}) => ReactNode;

/** Promoted columns nearly every type carries. */
const IssuedAndExpires = ({
  version,
  issuedLabel = "Issued",
  expiresLabel = "Expires",
}: {
  version: NonNullable<Credential["version"]>;
  issuedLabel?: string;
  expiresLabel?: string;
}) => (
  <>
    <DateRow label={issuedLabel} value={version.issuedAt} />
    <DateRow label={expiresLabel} value={version.expiresAt} />
  </>
);

/** The shape shared by "a number, a place, and two dates" credentials. */
const numberedCredential =
  (options: { numberLabel: string; showState?: boolean }): Renderer =>
  ({ version }) => (
    <Fields>
      <Row label={options.numberLabel} value={version.number} />
      {options.showState !== false && (
        <Row label="State" value={version.stateCode} />
      )}
      <IssuedAndExpires version={version} />
      <Row label="Status" value={version.status} />
    </Fields>
  );

const CREDENTIAL_DETAIL_RENDERERS: Record<CredentialType, Renderer> = {
  [CredentialType.StateLicense]: ({ version, details }) => (
    <>
      <Fields>
        <Row label="State" value={version.stateCode} />
        <Row label="License number" value={version.number} />
        <Row
          label="Discipline"
          value={
            details.licenseType
              ? (LICENSE_TYPE_LABELS[details.licenseType as LicenseType] ??
                String(details.licenseType))
              : null
          }
        />
        <IssuedAndExpires version={version} />
        <Row label="Status" value={version.status} />
        <Row
          label="Practising here"
          value={details.isCurrentlyPracticingInState as boolean | undefined}
        />
      </Fields>
      {details.compact === true && (
        <Section
          title="Interstate compact"
          rows={[
            { label: "Compact letter", value: "Yes" },
            {
              label: "States granted",
              value: joined(
                Array.isArray(details.imlcStateCodes)
                  ? details.imlcStateCodes
                  : [],
                ", ",
              ),
            },
          ]}
        />
      )}
    </>
  ),

  [CredentialType.Dea]: ({ version, details }) => (
    <Fields>
      <Row label="DEA number" value={version.number} />
      <Row label="State" value={version.stateCode} />
      <IssuedAndExpires version={version} />
      <Row label="Status" value={version.status} />
      <ChipsRow label="Schedules" values={details.schedules} />
      <Row label="Unlimited" value={details.licenseUnlimited as boolean} />
      <Row label="Activity" value={details.businessActivity as string} />
    </Fields>
  ),

  [CredentialType.Cds]: ({ version, details }) => (
    <Fields>
      <Row label="CDS number" value={version.number} />
      <Row label="State" value={version.stateCode} />
      <IssuedAndExpires version={version} />
      <Row label="Status" value={version.status} />
      <ChipsRow label="Schedules" values={details.schedules} />
      <Row
        label="Practising here"
        value={details.isCurrentlyPracticingInState as boolean}
      />
      <Row label="Notes" value={details.notes as string} />
    </Fields>
  ),

  [CredentialType.BoardCertification]: ({ version, details }) => {
    const listings = asDetails(details.directoryListings);
    const shown = ["hmo", "ppo", "pos"].filter((key) => listings[key] === true);
    return (
      <>
        <Fields>
          <Row label="Board" value={details.certifyingBoardName as string} />
          <Row label="Specialty" value={details.specialty as string} />
          <Row label="Level" value={details.specialtyLevel as string} />
          <Row label="Certificate" value={version.number} />
          <IssuedAndExpires version={version} />
          <Row label="Status" value={version.status} />
          <DateRow label="Exam date" value={details.boardExamDate} />
          <DateRow label="Recertified" value={details.recertificationDate} />
          <Row label="NUCC code" value={details.specialtyNuccCode as string} />
          <Row
            label="Not certified"
            value={details.notBoardCertifiedReason as string}
          />
          <Row
            label="Other focus"
            value={details.otherPracticeInterestOrFocus as string}
          />
        </Fields>
        <Section
          title="Directory listings"
          rows={[
            {
              label: "Listed in",
              value: joined(
                shown.map((key) => key.toUpperCase()),
                ", ",
              ),
            },
          ]}
        />
      </>
    );
  },

  [CredentialType.Ecfmg]: ({ version }) => (
    <Fields>
      <Row label="Certificate" value={version.number} />
      <DateRow label="Issued" value={version.issuedAt} />
    </Fields>
  ),

  [CredentialType.Education]: ({ version, details }) => (
    <>
      <Fields>
        <Row label="Kind" value={details.kind as string} />
        <Row label="Institution" value={details.institutionName as string} />
        <Row label="Degree" value={details.degree as string} />
        <Row label="Program" value={details.program as string} />
        <Row label="Specialty" value={details.specialty as string} />
        <DateRow label="Started" value={details.startDate} />
        <DateRow
          label="Completed"
          value={details.endDate ?? version.issuedAt}
        />
        <Row label="Primary" value={details.isPrimary as boolean} />
        <AddressRow label="Address" address={details.address} />
        <Row
          label="Location"
          value={[details.city, details.country].filter(Boolean).join(", ")}
        />
      </Fields>
      <RepeatingSection
        title="USMLE steps"
        items={details.usmleSteps}
        renderItem={(step) => (
          <>
            <Row label="Step" value={step.step as string} />
            <Row label="Passed" value={step.passed as boolean} />
            <Row label="Score" value={step.score as number} />
            <DateRow label="Taken" value={step.examDate} />
          </>
        )}
      />
    </>
  ),

  [CredentialType.Training]: ({ version, details }) => (
    <Fields>
      <Row label="Type" value={details.trainingType as string} />
      <Row label="Institution" value={details.institutionName as string} />
      <Row label="Specialty" value={details.specialty as string} />
      <Row label="Degree" value={details.degree as string} />
      <Row label="Program" value={details.program as string} />
      <Row
        label="Affiliated with"
        value={details.affiliatedUniversity as string}
      />
      <DateRow label="Started" value={details.startDate} />
      <DateRow label="Completed" value={details.endDate ?? version.issuedAt} />
      <Row
        label="Completed?"
        value={details.isProgramSuccessfullyCompleted as boolean}
      />
      <Row label="Director" value={details.programDirector as string} />
      <Row
        label="Current director"
        value={details.currentProgramDirector as string}
      />
      <AddressRow label="Address" address={details.address} />
      <Row
        label="Location"
        value={[details.city, details.country].filter(Boolean).join(", ")}
      />
    </Fields>
  ),

  [CredentialType.LifeSupport]: ({ version, details }) => (
    <Fields>
      <Row label="Certification" value={details.certKind as string} />
      <Row label="Issuing body" value={details.issuingBody as string} />
      <Row label="Card number" value={version.number} />
      <Row label="eCard code" value={details.eCardCode as string} />
      <IssuedAndExpires version={version} />
    </Fields>
  ),

  [CredentialType.HealthScreening]: ({ version, details }) => (
    <Fields>
      <Row label="Screening" value={details.screeningKind as string} />
      <Row label="Result" value={details.result as string} />
      <Row label="Method" value={details.method as string} />
      <Row label="Facility" value={details.facility as string} />
      <Row label="Declined" value={details.declination as boolean} />
      <IssuedAndExpires version={version} issuedLabel="Taken" />
    </Fields>
  ),

  [CredentialType.BirthCertificate]: numberedCredential({
    numberLabel: "Certificate number",
    showState: false,
  }),

  [CredentialType.Passport]: ({ version, details }) => (
    <Fields>
      <Row label="Passport number" value={version.number} />
      <Row label="Issuing country" value={details.issuingCountry as string} />
      <IssuedAndExpires version={version} />
    </Fields>
  ),

  [CredentialType.DriversLicense]: numberedCredential({
    numberLabel: "Licence number",
  }),

  [CredentialType.GovernmentId]: ({ version, details }) => (
    <Fields>
      <Row label="ID type" value={details.idKind as string} />
      <Row label="ID number" value={version.number} />
      <Row label="Issuer" value={details.issuer as string} />
      <IssuedAndExpires version={version} />
    </Fields>
  ),

  [CredentialType.MalpracticeInsurance]: ({ version, details }) => (
    <>
      <Fields>
        <Row label="Carrier" value={details.carrierName as string} />
        <Row label="Policy number" value={version.number} />
        <IssuedAndExpires version={version} issuedLabel="Effective" />
        <Row label="Status" value={version.status} />
        <Row label="Coverage" value={details.coverageType as string} />
        <Row label="Per occurrence" value={money(details.perOccurrenceLimit)} />
        <Row label="Aggregate" value={money(details.aggregateLimit)} />
        <Row label="Self-insured" value={details.isSelfInsured as boolean} />
        <Row
          label="With carrier"
          value={details.lengthOfTimeWithCarrier as string}
        />
      </Fields>
      <Section
        title="Carrier contact"
        rows={[
          { label: "Phone", value: details.carrierPhone as string },
          { label: "Address", value: addressLine(details.carrierAddress) },
        ]}
      />
    </>
  ),

  [CredentialType.MalpracticeClaimsHistory]: ({ details }) => (
    <>
      <Fields>
        <Row label="Claims" value={details.noClaims === true ? "None" : null} />
        <DateRow label="Disclosed" value={details.disclosureDate} />
      </Fields>
      <RepeatingSection
        title="Claims"
        items={details.claims}
        renderItem={(claim) => (
          <>
            <Row label="Carrier" value={claim.carrier as string} />
            <Row label="Status" value={claim.status as string} />
            <Row label="Amount" value={money(claim.amount)} />
            <DateRow label="Occurred" value={claim.occurrenceDate} />
            <DateRow label="Filed" value={claim.filedDate} />
            <DateRow label="Resolved" value={claim.resolvedDate} />
            <Row label="Details" value={claim.description as string} />
          </>
        )}
      />
    </>
  ),

  [CredentialType.WorkHistory]: ({ details }) => {
    const contact = asDetails(details.contact);
    const isGap = details.kind === "gap";
    return (
      <>
        <Fields>
          <Row label="Kind" value={isGap ? "Gap in work" : "Employment"} />
          <Row label="Employer" value={details.employerName as string} />
          <Row label="Position" value={details.position as string} />
          <DateRow label="Started" value={details.startDate} />
          <DateRow label="Ended" value={details.endDate} />
          <Row label="Current" value={details.currentlyEmployed as boolean} />
          <Row
            label="Location"
            value={[details.city, details.state, details.country]
              .filter(Boolean)
              .join(", ")}
          />
          <AddressRow label="Address" address={details.address} />
          <Row
            label="Responsibilities"
            value={details.responsibilities as string}
          />
          <Row
            label={isGap ? "Explanation" : "Reason for leaving"}
            value={
              (isGap
                ? details.gapExplanation
                : details.reasonForDiscontinuance) as string
            }
          />
        </Fields>
        <Section
          title="Reference contact"
          rows={[
            { label: "Phone", value: contact.phone as string },
            { label: "Email", value: contact.email as string },
            { label: "Notes", value: contact.info as string },
          ]}
        />
      </>
    );
  },

  [CredentialType.HospitalAffiliation]: ({ details }) => {
    const contact = asDetails(details.contact);
    const admitter = asDetails(details.admitter);
    return (
      <>
        <Fields>
          <Row label="Hospital" value={details.hospitalName as string} />
          <Row label="Affiliation" value={details.affiliationKind as string} />
          <Row label="Department" value={details.department as string} />
          <Row label="Primary" value={details.primaryHospital as boolean} />
          <Row
            label="Privilege type"
            value={details.admittingPrivilegeType as string}
          />
          <Row
            label="% of practice"
            value={
              typeof details.hospitalPercentage === "number"
                ? `${details.hospitalPercentage}%`
                : null
            }
          />
          <DateRow label="Started" value={details.startDate} />
          <DateRow label="Ended" value={details.endDate} />
          <Row
            label="Location"
            value={[details.city, details.country].filter(Boolean).join(", ")}
          />
          <AddressRow label="Address" address={details.address} />
          <Row label="Description" value={details.description as string} />
          <Row
            label="Arrangement"
            value={details.arrangementDescription as string}
          />
          <Row
            label="Reason ended"
            value={details.reasonsForDiscontinuance as string}
          />
        </Fields>
        <Section
          title="Hospital contact"
          rows={[
            {
              label: "Name",
              value: joined([
                contact.firstName,
                contact.middleInitial,
                contact.lastName,
              ]),
            },
            { label: "Email", value: contact.email as string },
            { label: "Phone", value: contact.phone as string },
            { label: "Fax", value: contact.fax as string },
          ]}
        />
        <Section
          title="Admitting physician"
          rows={[
            { label: "Name", value: admitter.name as string },
            { label: "NPI", value: admitter.npi as string },
            { label: "Group", value: admitter.groupName as string },
            { label: "Group NPI", value: admitter.organizationNpi as string },
            { label: "Email", value: admitter.email as string },
            { label: "Phone", value: admitter.phone as string },
            {
              label: "Same specialty",
              value: details.sameSpecialtyAsAdmitter as boolean,
            },
          ]}
        />
      </>
    );
  },

  [CredentialType.CaseLog]: ({ details }) => (
    <Fields>
      <Row label="Facility" value={details.facility as string} />
      <Row label="Specialty" value={details.specialty as string} />
      <Row label="Procedures" value={details.procedureCount as number} />
      <DateRow label="Period from" value={details.periodStart} />
      <DateRow label="Period to" value={details.periodEnd} />
    </Fields>
  ),

  [CredentialType.Attestation]: ({ version, details }) => {
    const responses = asDetails(details.responses);
    const answered = Object.keys(responses).length;
    return (
      <>
        <Fields>
          <Row label="Method" value={details.method as string} />
          <DateRow label="Signed" value={details.signedAt} />
          <DateRow label="Valid until" value={version.expiresAt} />
          <Row
            label="Questionnaire"
            value={details.questionnaireVersion as string}
          />
          <Row label="Vendor ref" value={details.vendorRef as string} />
        </Fields>
        {/* The responses are an open record whose keys are the vendor's
            question ids — listing them raw would be unreadable, so the count
            stands in until the questionnaire itself is modelled. */}
        <Section
          title="Questionnaire"
          rows={[
            {
              label: "Answers",
              value: answered > 0 ? `${answered} recorded` : null,
            },
          ]}
        />
      </>
    );
  },

  [CredentialType.BackgroundCheck]: ({ version, details }) => (
    <Fields>
      <Row label="Check" value={details.checkKind as string} />
      <Row label="Vendor" value={details.vendor as string} />
      <Row label="Package" value={details.packageKind as string} />
      <Row label="Report ID" value={details.reportId as string} />
      <DateRow label="Completed" value={details.completedAt} />
      <DateRow label="Valid until" value={version.expiresAt} />
    </Fields>
  ),

  [CredentialType.PeerReference]: ({ version, details }) => (
    <Fields>
      <Row label="Referee" value={details.refereeName as string} />
      <Row label="Title" value={details.refereeTitle as string} />
      <Row label="Specialty" value={details.refereeSpecialty as string} />
      <Row label="Relationship" value={details.relationship as string} />
      <Row label="Years known" value={details.yearsKnown as number} />
      <Row label="Email" value={details.contactEmail as string} />
      <Row label="Phone" value={details.contactPhone as string} />
      <DateRow label="Completed" value={details.completedAt} />
      <DateRow label="Valid until" value={version.expiresAt} />
    </Fields>
  ),

  [CredentialType.CollaborativeAgreement]: ({ version, details }) => (
    <Fields>
      <Row
        label="Supervising"
        value={details.supervisingPhysicianName as string}
      />
      <Row
        label="Their NPI"
        value={details.supervisingPhysicianNpi as string}
      />
      <Row label="Agreement" value={details.agreementKind as string} />
      <Row label="Setting" value={details.practiceSetting as string} />
      <Row label="Filing number" value={version.number} />
      <IssuedAndExpires version={version} issuedLabel="Effective" />
      <Row label="Status" value={version.status} />
    </Fields>
  ),

  [CredentialType.Npi]: ({ version, details }) => (
    <Fields>
      <Row label="NPI" value={version.number} />
      <DateRow label="Enumerated" value={details.enumerationDate} />
      <Row label="UPIN" value={details.upin as string} />
      <ChipsRow label="Taxonomy" values={details.taxonomyCodes} />
    </Fields>
  ),

  [CredentialType.MedicaidId]: numberedCredential({
    numberLabel: "Medicaid ID",
  }),

  [CredentialType.MedicareId]: ({ version, details }) => (
    <Fields>
      <Row label="Medicare ID" value={version.number} />
      <Row label="Kind" value={details.kind as string} />
      <Row label="State" value={version.stateCode} />
    </Fields>
  ),

  [CredentialType.PersonalInfo]: ({ details }) => {
    const name = asDetails(details.name);
    const birthPlace = asDetails(details.birthPlace);
    const physical = asDetails(details.physical);
    const citizenship = asDetails(details.citizenship);
    const contact = asDetails(details.contact);
    const professional = asDetails(details.professional);
    const military = asDetails(details.military);
    const join = (value: unknown) =>
      Array.isArray(value) ? value.filter(Boolean).join(", ") : null;

    return (
      <>
        <Fields>
          <Row
            label="Name"
            value={[name.first, name.middle, name.last, name.suffix]
              .filter(Boolean)
              .join(" ")}
          />
          <Row label="Also known as" value={join(details.otherNames)} />
          <DateRow label="Born" value={details.birthDate} />
          <Row
            label="Birthplace"
            value={[birthPlace.city, birthPlace.state, birthPlace.country]
              .filter(Boolean)
              .join(", ")}
          />
          <Row label="Gender" value={details.gender as string} />
          <Row label="Race" value={details.race as string} />
          <Row label="CAQH ID" value={details.caqhId as string} />
        </Fields>
        <Section
          title="Contact"
          rows={[
            { label: "Email", value: join(contact.emails) },
            { label: "Phone", value: join(contact.phones) },
            { label: "Home", value: addressLine(details.homeAddress) },
            {
              label: "Correspondence",
              value: addressLine(details.correspondenceAddress),
            },
          ]}
        />
        <Section
          title="Practice"
          rows={[
            {
              label: "Provider type",
              value: professional.providerType as string,
            },
            { label: "Role", value: professional.providerRole as string },
            { label: "Setting", value: professional.practiceSetting as string },
            {
              label: "Primary state",
              value: professional.primaryPracticeState as string,
            },
            {
              label: "Other states",
              value: join(professional.additionalPracticeStates),
            },
            { label: "Languages", value: join(professional.languages) },
          ]}
        />
        <Section
          title="Citizenship"
          rows={[
            { label: "US citizen", value: citizenship.usCitizen as boolean },
            { label: "Country", value: citizenship.country as string },
            { label: "Visa", value: citizenship.visaStatus as string },
            { label: "Visa number", value: citizenship.visaNumber as string },
            {
              label: "Work eligible",
              value: citizenship.eligibleToWorkInUs as boolean,
            },
          ]}
        />
        <Section
          title="Physical"
          rows={[
            {
              label: "Height",
              value:
                typeof physical.heightInches === "number"
                  ? `${Math.floor(physical.heightInches / 12)}' ${physical.heightInches % 12}"`
                  : null,
            },
            {
              label: "Weight",
              value:
                typeof physical.weightLbs === "number"
                  ? `${physical.weightLbs} lb`
                  : null,
            },
            { label: "Hair", value: physical.hairColor as string },
            { label: "Eyes", value: physical.eyeColor as string },
          ]}
        />
        <Section
          title="Military service"
          rows={[
            { label: "Served", value: military.served as boolean },
            { label: "Branch", value: military.branch as string },
            { label: "Status", value: military.activeOrReserve as string },
            { label: "From", value: dateValue(military.serviceStart) },
            { label: "To", value: dateValue(military.serviceEnd) },
            { label: "Last posting", value: military.lastLocation as string },
          ]}
        />
      </>
    );
  },

  [CredentialType.Cv]: ({ version }) => (
    <Fields>
      <DateRow label="Dated" value={version.issuedAt} />
    </Fields>
  ),

  [CredentialType.Other]: ({ version }) => (
    <Fields>
      {/* Same word the form asks for. "Reference" here against "Document name"
          there would read as two different fields. */}
      <Row label="Document name" value={version.number} />
      <IssuedAndExpires version={version} />
    </Fields>
  ),
};

/**
 * A credential's full contents, laid out for its type.
 *
 * Handles the empty case itself: a document-substance credential like a CV has
 * no structured data behind it, and saying so is more useful than an empty
 * panel.
 */
export function CredentialDetailBody({
  credential,
}: {
  credential: Credential;
}) {
  const version = credential.version;
  const renderer =
    CREDENTIAL_DETAIL_RENDERERS[credential.credentialType as CredentialType];

  const body = version
    ? renderer?.({ version, details: asDetails(version.details) })
    : null;

  if (!body) {
    return (
      <p className="text-sm text-gray-500">
        This credential is the document itself — there are no other details
        recorded.
      </p>
    );
  }
  return <>{body}</>;
}
